import React, { useMemo, useState } from 'react';

import { Col } from '@opendoor/atoms/box';
import { isCnmlClosedResale } from 'components/hub/homeResale/homeResale';
import type { HubSellingTransaction } from 'components/hub/useHubSellerTransactions';

import { HubFilterDropdown, resolveAvailableFilter } from './HubFilterDropdown';
import { HubSectionHeader } from './HubSectionHeader';
import { isHomeSaleSectionVisible } from './hubSectionVisibility';
import { TransactionCardSkeleton } from './HubSkeletons';
import {
  buildPastSaleTransactionCardModel,
  buildSaleTransactionCardModel,
} from './saleTransactionCardModel';
import { TransactionCard } from './TransactionCard';
import type { TransactionCardModel } from './TransactionCard';

type SaleFilter = 'active' | 'past';

const SALE_FILTER_OPTIONS: ReadonlyArray<{ value: SaleFilter; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'past', label: 'Expired' },
];

interface HomeSaleSectionProps {
  transactions: HubSellingTransaction[];
  isLoading: boolean;
}

const isTransactionCardModel = (
  model: TransactionCardModel | null,
): model is TransactionCardModel => Boolean(model);

export const HomeSaleSection: React.FC<HomeSaleSectionProps> = ({ transactions, isLoading }) => {
  const [selectedFilter, setSelectedFilter] = useState<SaleFilter | null>(null);

  // One sale in flight, plus any home still being resold.
  const activeSaleTransactions = useMemo(() => {
    const saleInFlight = transactions
      .filter((transaction) => !isCnmlClosedResale(transaction))
      .map((transaction) => buildSaleTransactionCardModel(transaction))
      .find(isTransactionCardModel);
    const awaitingResale = transactions
      .filter(isCnmlClosedResale)
      .map((transaction) => buildSaleTransactionCardModel(transaction))
      .filter(isTransactionCardModel);
    return [...(saleInFlight ? [saleInFlight] : []), ...awaitingResale];
  }, [transactions]);
  const pastSaleTransactions = useMemo(
    () =>
      transactions
        .map((transaction) => buildPastSaleTransactionCardModel(transaction))
        .filter(isTransactionCardModel),
    [transactions],
  );

  if (!isHomeSaleSectionVisible(transactions, isLoading)) {
    return null;
  }

  const filter = resolveAvailableFilter(selectedFilter, [
    { value: 'active', available: activeSaleTransactions.length > 0 },
    { value: 'past', available: pastSaleTransactions.length > 0 },
  ]);
  const showFilter =
    !isLoading && activeSaleTransactions.length > 0 && pastSaleTransactions.length > 0;
  const visibleTransactions = filter === 'active' ? activeSaleTransactions : pastSaleTransactions;

  return (
    <Col className="w-full gap-4">
      <HubSectionHeader
        title="Home sale"
        action={
          showFilter ? (
            <HubFilterDropdown
              value={filter}
              options={SALE_FILTER_OPTIONS}
              onChange={setSelectedFilter}
              ariaLabel="Filter sale offers"
            />
          ) : undefined
        }
      />

      {isLoading ? (
        <TransactionCardSkeleton />
      ) : (
        visibleTransactions.map((transaction) => (
          <TransactionCard key={transaction.id} section="home-sale" transaction={transaction} />
        ))
      )}
    </Col>
  );
};
